import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  Users,
  Globe,
  BarChart3,
  Target,
  Shield,
  CheckCircle,
  ArrowRight,
  DollarSign,
  Briefcase,
  Zap,
  ClipboardCheck,
  TrendingDown,
  Crown,
  FileCheck,
  AlertTriangle,
  Star
} from 'lucide-react'
import SEO from '@components/SEO'
import { useContent } from '@context/ContentContext'
import Reveal from '@components/dimensional/Reveal'
import BandArchitecture from '@components/dimensional/BandArchitecture'
import RewardsArchitecture from '@components/dimensional/RewardsArchitecture'
import BrandPrimitives from '@components/dimensional/BrandPrimitives'
import TiltCard from '@components/dimensional/TiltCard'

// Icon mapping for services
const iconMap = {
  appraisals: ClipboardCheck,
  increment: TrendingUp,
  variable: Target,
  esop: TrendingDown,
  cxo: Crown,
  benefits: Shield,
  policies: FileCheck,
  compliance: AlertTriangle,
  rewards: Star,
  // Legacy mappings for backward compatibility
  equity: TrendingUp,
  executive: Users,
  regional: Globe,
  benchmarking: BarChart3
}

const ServicesPage = () => {
  const { content, loading } = useContent()

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const services = [
    {
      id: 'equity',
      icon: TrendingUp,
      title: 'Equity & Long-Term Incentives',
      subtitle: 'Option pools, RSUs, ESOPs and the vesting mechanics underneath them',
      description: 'We size the pool, model the dilution, and settle the grant mechanics before a single letter goes out. Cliff dates, refresh cycles, exercise windows, 409A timing — the details that get expensive when they are decided late.',
      subServices: [
        {
          name: 'Plan Design & Structure',
          description: 'RSUs or options, and the reasoning behind the choice. Structure mapped to your funding stage, jurisdiction mix and how long people actually stay.'
        },
        {
          name: 'Pool Sizing & Allocation',
          description: 'Pool modelling by level and role, allocation tiers, and cap table simulations run across funding and exit scenarios.'
        },
        {
          name: 'Grant Practice & Vesting',
          description: 'Grant timing, cliff length, refresh cadence, and performance-linked vesting where the milestone is measurable.'
        },
        {
          name: 'Long-Term Incentive Design',
          description: 'Multi-year plans tied to metrics a board can audit — revenue, EBITDA, TSR or operating milestones with a defined finish line.'
        },
        {
          name: 'Employee Education',
          description: 'Sessions that explain strike price, tax events and exercise windows in plain language. People value what they understand.'
        },
        {
          name: 'Regulatory & Tax Compliance',
          description: '409A valuations, securities and tax treatment per jurisdiction, cross-border grant mapping for distributed teams.'
        },
        {
          name: 'Retention Modelling',
          description: 'Where the cliffs land against your attrition data, and what a targeted refresh grant is worth against a live counter-offer.'
        }
      ],
      features: [
        'Option & RSU plan design',
        'ESOP structuring',
        'Pool sizing & dilution modelling',
        'Cap table scenario analysis',
        'Cliff & vesting schedules',
        '409A and cross-border compliance',
        'Grant letters & employee comms',
        'Refresh and retention grants',
        'Liquidity and exit modelling'
      ],
      benefits: [
        'Grants sized before the offer, not during the negotiation',
        'Dilution the board has seen modelled rather than discovered',
        'Vesting that holds people through the years that matter',
        'Tax and securities treatment settled in every country you hire in',
        'Employees who can explain their own grant without HR in the room'
      ]
    },
    {
      id: 'executive',
      icon: Users,
      title: 'Executive Pay & IPO Readiness',
      subtitle: 'C-suite packages, NRC papers and the disclosure a listing demands',
      description: 'Executive pay is read by boards, regulators and eventually the public. We build packages that survive all three: peer groups you can defend, pay mix tied to performance, and disclosure groundwork done long before the bankers ask for it.',
      subServices: [
        {
          name: 'Pay Philosophy & Positioning',
          description: 'Where you sit against the market at each level, what you deliberately pay above it for, and the written principles that settle next year’s argument.'
        },
        {
          name: 'C-Suite Package Design',
          description: 'Base, target bonus, LTI mix, perquisites and severance. Succession and external hire packages costed side by side.'
        },
        {
          name: 'Peer Group & Benchmarking',
          description: 'Peer sets built on revenue, headcount and sector rather than convenience. Quantum and pay mix compared line by line.'
        },
        {
          name: 'IPO Readiness & Diligence',
          description: 'Pre-listing reward audit, grant clean-up, CD&A drafting, and the equity story investors will interrogate.'
        },
        {
          name: 'Governance & Board Support',
          description: 'NRC papers, say-on-pay preparation, SOX controls, and documentation that holds up under an auditor.'
        },
        {
          name: 'Retention Through the Event',
          description: 'Lock-in structures and milestone-linked awards that keep the leadership team intact through the listing and the year after it.'
        }
      ],
      features: [
        'C-suite package design',
        'Peer group construction',
        'IPO & listing readiness',
        'Board and NRC papers',
        'Say-on-pay preparation',
        'CD&A and disclosure drafting',
        'Executive retention structures',
        'SOX and internal controls',
        'Severance & change-in-control terms'
      ],
      benefits: [
        'Pay decisions the board can defend in writing',
        'Disclosure ready before diligence starts, not during it',
        'Peer groups that hold up under investor questioning',
        'Leadership held through the listing window',
        'Governance gaps closed while they are still cheap to close'
      ]
    },
    {
      id: 'variable',
      icon: Target,
      title: 'Variable Pay & Sales Compensation',
      subtitle: 'Quotas, accelerators, caps and clawbacks, tested against last year’s actuals',
      description: 'A bonus plan is a forecast of behaviour. We build the payout curves, run them against historical attainment, and show you what the plan costs at 70, 100 and 140 percent of target before anyone signs it.',
      subServices: [
        {
          name: 'Short-Term Incentive Design',
          description: 'Target percentages by level, weighted scorecards, and the funding rule that decides what the pool pays when the year lands short.'
        },
        {
          name: 'Sales Plan Architecture',
          description: 'Quota setting by territory and segment, commission rates, accelerators and decelerators, ramp terms for new hires.'
        },
        {
          name: 'Payout Curve Modelling',
          description: 'Every plan run against prior-year attainment distributions. Cost at threshold, target and stretch, on the table before approval.'
        },
        {
          name: 'Metric & Scorecard Design',
          description: 'Metrics people can move and finance can verify. Fewer of them, weighted honestly, with a defined data source each.'
        },
        {
          name: 'Sales Operations Enablement',
          description: 'Crediting rules, CRM and payout system linkage, dispute handling, clawback and windfall provisions.'
        },
        {
          name: 'Rollout & Manager Enablement',
          description: 'Plan documents, calculators and manager scripts, so the first payout cycle does not turn into a helpdesk queue.'
        }
      ],
      features: [
        'STI and bonus plan design',
        'Quota and territory models',
        'Commission and accelerator structures',
        'Attainment-based cost modelling',
        'Scorecard and KPI design',
        'Crediting and clawback rules',
        'CRM and payout integration',
        'Plan documents and calculators',
        'Manager briefing toolkits'
      ],
      benefits: [
        'Plan cost known at threshold, target and stretch before sign-off',
        'Payout curves that reward performance rather than a soft quota',
        'Fewer disputes, because crediting rules are written down',
        'Sales leadership and finance working from one model',
        'Windfalls and clawbacks handled in the plan, not in an email thread'
      ]
    },
    {
      id: 'benefits',
      icon: Shield,
      title: 'Employee Benefits Design',
      subtitle: 'Cover, retirals, leave and allowances — costed and weighted to what gets used',
      description: 'Benefits budgets drift and programmes accumulate. We audit utilisation, price each element against the market, and rebuild the package around what people actually claim rather than what looked good in a slide.',
      subServices: [
        {
          name: 'Health, Insurance & Well-being',
          description: 'Medical, dental and vision cover, life and accident, critical illness, mental health support, annual screening. Sums insured tested against real claim patterns.'
        },
        {
          name: 'Financial, Mobility & Asset Benefits',
          description: 'Car lease schemes, device and home-office allowances, connectivity reimbursement, commuting support, salary-linked emergency loans.'
        },
        {
          name: 'Leave, Relocation & Flexibility',
          description: 'PTO design, parental and caregiving leave, sabbaticals, relocation terms, and hybrid policy HR can administer without case-by-case rulings.'
        },
        {
          name: 'Learning & Career Benefits',
          description: 'Tuition support, certification funding, conference budgets and coaching, with repayment terms where the spend is material.'
        },
        {
          name: 'Family, Lifestyle & Life Events',
          description: 'Childcare and education support, marriage and bereavement leave, pet policies, festival and gifting programmes.'
        },
        {
          name: 'Recognition, Discounts & Perks',
          description: 'Spot awards, long-service recognition, referral bonuses, meal and travel subsidies, peer recognition platforms that people log into twice.'
        },
        {
          name: 'Cost & Utilisation Analytics',
          description: 'Take-up by benefit and demographic, cost per employee by location, vendor selection and renewal negotiation.'
        }
      ],
      features: [
        'Health and insurance programme design',
        'Retirals and financial wellbeing',
        'Leave and flexibility policy',
        'Learning and development benefits',
        'Family and caregiving support',
        'Recognition and perks',
        'Utilisation and cost analytics',
        'Vendor selection and renewals',
        'Multi-country benefit harmonisation'
      ],
      benefits: [
        'Spend redirected from unused benefits toward the ones people claim',
        'Cover levels benchmarked against your sector, not a national average',
        'Policy wording HR can apply without escalating every case',
        'Renewals negotiated with utilisation data in hand',
        'One benefits story across every location you hire in'
      ]
    },
    {
      id: 'benchmarking',
      icon: BarChart3,
      title: 'Pay Structures & Market Benchmarking',
      subtitle: 'Job architecture, salary bands and market data you can defend in a hostile room',
      description: 'Ad-hoc pay decisions stop scaling somewhere around a hundred people. We build the levelling framework, cut the market data by role and city, and set bands with midpoints your managers can hire against.',
      subServices: [
        {
          name: 'Market Benchmarking',
          description: 'Survey participation and cuts by role, level and location. Where published data runs thin, we run bespoke peer sampling.'
        },
        {
          name: 'Job Architecture & Bands',
          description: 'Job families, levels and geo differentials. Bands with midpoints, range width and overlap built to hold for two cycles.'
        },
        {
          name: 'Pay Philosophy',
          description: 'Target percentile by function, what you pay above market for, and how the answer should change as you scale.'
        },
        {
          name: 'Job Evaluation & Levelling',
          description: 'Point-factor or bespoke evaluation applied consistently, so a Level 5 means the same thing in engineering and in finance.'
        },
        {
          name: 'Pay Equity Audits',
          description: 'Regression-based gap analysis by gender and tenure, with a costed remediation plan attached to the finding.'
        },
        {
          name: 'Merit & Increment Strategy',
          description: 'Merit matrices driven by rating and range penetration, budget scenarios, and forecasting tools for the cycle.'
        },
        {
          name: 'Communication & Manager Training',
          description: 'Band transparency decisions, compensation letters, and scripts for the conversations that follow the letter.'
        }
      ],
      features: [
        'Custom and published market surveys',
        'Job families and levelling',
        'Salary bands and midpoints',
        'Geo-differential design',
        'Pay philosophy documentation',
        'Pay equity regression analysis',
        'Merit matrix and budget modelling',
        'Compensation letters and comms',
        'Manager training toolkits'
      ],
      benefits: [
        'A structure managers can hire against without asking HR',
        'Market data cut to your roles and cities, not a national median',
        'Pay gaps quantified and costed before anyone else finds them',
        'Merit budgets that land where the performance did',
        'One levelling language across every function'
      ]
    },
    {
      id: 'rewards',
      icon: Star,
      title: 'Total Rewards Strategy & Performance',
      subtitle: 'One reward architecture, and the performance system that feeds it',
      description: 'Equity, bonus, benefits and merit are usually run by people who never compare notes. We connect them to a single philosophy and one performance system, then build the statements that show an employee what the whole package is worth.',
      subServices: [
        {
          name: 'Rewards Philosophy & Architecture',
          description: 'One set of principles across pay, equity, benefits and recognition, tied to what the business wants to be known for.'
        },
        {
          name: 'Performance & Appraisal Systems',
          description: 'Rating scales, calibration mechanics, continuous check-in models, and 360 feedback where it earns its cost.'
        },
        {
          name: 'Goal Setting Frameworks',
          description: 'OKR or cascade design, goal quality standards, and mid-year mechanics for when the plan inevitably changes.'
        },
        {
          name: 'Manager Capability',
          description: 'Calibration training, bias interruption in reviews, and the language a manager needs for a difficult increment conversation.'
        },
        {
          name: 'Rewards Technology & Analytics',
          description: 'Tooling selection, HRMS integration, and reporting that answers a board question without a week of spreadsheet work.'
        },
        {
          name: 'Mobility & M&A Harmonisation',
          description: 'Assignment and relocation reward models, and post-merger harmonisation of two pay structures into one.'
        },
        {
          name: 'Employee Communication',
          description: 'Personalised total rewards statements and campaigns that make the invisible half of the package visible.'
        }
      ],
      features: [
        'Total rewards philosophy',
        'Performance management design',
        'Calibration and rating models',
        'OKR and goal frameworks',
        'Manager training programmes',
        'Rewards technology and analytics',
        'Global mobility reward models',
        'Post-merger harmonisation',
        'Total rewards statements'
      ],
      benefits: [
        'One philosophy sitting behind every reward decision',
        'Calibration that produces ratings people will stand behind',
        'Managers who can explain an increment without escalating it',
        'Reward data in one place, reportable to the board',
        'Employees who can see what the full package is worth'
      ]
    }
  ]

  const industries = [
    { name: 'Technology', icon: Zap, description: 'Equity-heavy offers, headcount doubling mid-cycle, and bands that have to hold while the org chart moves.' },
    { name: 'Financial Services', icon: DollarSign, description: 'Deferral, malus and clawback under a regulator’s eye. Incentives that reward the right kind of risk.' },
    { name: 'Professional Services', icon: Briefcase, description: 'Partner economics, utilisation-linked pay, and reward tied to client outcomes rather than headcount.' },
    { name: 'Healthcare', icon: Shield, description: 'Clinical and commercial roles on one structure, shift and on-call premiums, retention in a thin talent pool.' }
  ]

  // Stats removed - focus on methodology and expertise

  // Get page content
  const pageContent = content.pages?.services || {}
  const heroTitle = pageContent.hero?.title || 'Total Rewards Consulting Excellence'
  const heroSubtitle = pageContent.hero?.subtitle || 'Specialized total rewards consulting across 6 core areas: equity compensation, executive pay, variable pay design, benefits strategy, market benchmarking, and integrated rewards programs. Strategic compensation solutions that drive business results.'

  // Get process content from Firestore
  const processContent = pageContent.process || {}
  const processTitle = processContent.title || 'Our Proven Methodology'
  const processSubtitle = processContent.subtitle || 'A systematic approach that ensures successful outcomes and sustainable results'
  const processSteps = processContent.steps || [
    {
      step: '01',
      title: 'Discovery & Assessment',
      description: 'We read the plan documents, the payout history and the attrition data before we offer an opinion. You get a written baseline of what is working and what is quietly costing you.'
    },
    {
      step: '02',
      title: 'Strategy & Design',
      description: 'Structures built for your business, modelled at the numbers that matter, and reviewed with the people who will have to defend them.'
    },
    {
      step: '03',
      title: 'Implementation & Support',
      description: 'Rollout, manager briefings and the first live cycle. We stay through the payout run, when the questions actually arrive.'
    }
  ]

  // Use services from content or fallback to static services
  const displayServices = content.services && content.services.length > 0 ? content.services : services

  if (loading) {
    return (
      <div className="pt-14 min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400 font-open-sans">Loading services...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-14 bg-black">
      <SEO
        title="Comprehensive Total Rewards Consulting - Expert Compensation & Benefits Solutions"
        description="Complete total rewards consulting including equity compensation & LTIs, executive compensation & IPO readiness, variable pay & sales compensation, comprehensive employee benefits, compensation consulting & benchmarking, and integrated performance management solutions."
        keywords="total rewards consulting, equity compensation, long term incentives, executive compensation, IPO readiness, variable pay, sales compensation, employee benefits, compensation consulting, market benchmarking, performance management, ESOP design, stock options, incentive plans, salary structure design, pay equity audits"
      />

      {/* Hero Section */}
      <section className="pt-14 lg:pt-16 pb-20 lg:pb-28 text-white relative overflow-hidden grain">
        {/* Background Image with WebP fallback */}
        <picture className="absolute inset-0">
          <source srcSet="/services-hero.webp" type="image/webp" />
          <img
            src="/services-hero.jpg"
            alt="Professional corporate office building interior"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Depth stack: base darkening, drifting gold mesh, then a vignette that
            pushes the frame edges back and holds the eye on the headline. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/75 to-black/95 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 mesh-drift opacity-70 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(118% 80% at 50% 4%, transparent 24%, rgba(0,0,0,0.58) 60%, rgba(0,0,0,0.94) 100%)'
          }}
        />

        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <motion.div
            className="text-center max-w-page mx-auto"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div
              className="inline-flex items-center gap-3 surface-raised-gold edge-lit rounded-pill px-5 py-2.5 mb-10"
              variants={fadeInUp}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_10px_rgba(255,209,24,0.9)]" aria-hidden="true"></span>
              <span className="text-mono-label text-primary-300">
                What We Do
              </span>
            </motion.div>

            <motion.h1
              className="text-display-xl text-white mb-8 text-balance"
              variants={fadeInUp}
            >
              {heroTitle}
            </motion.h1>

            <motion.p
              className="text-body-large text-neutral-300 mb-10 max-w-3xl mx-auto"
              variants={fadeInUp}
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={fadeInUp}
            >
              <Link to="/contact" className="btn-accent sheen px-8 py-3.5">
                Schedule Consultation
                <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
              </Link>
              <button
                onClick={() => {
                  const element = document.getElementById('services')
                  if (element) {
                    const navbarHeight = 80
                    const elementPosition = element.offsetTop - navbarHeight
                    window.scrollTo({
                      top: elementPosition,
                      behavior: 'smooth'
                    })
                  }
                }}
                className="inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
              >
                Explore Services
              </button>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 hairline" aria-hidden="true"></div>
      </section>

      {/* Service index — the page is long, so give people a way in */}
      <section className="bg-black relative overflow-hidden py-12 lg:py-16" aria-label="Jump to a service">
        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="surface-inset rounded-card-lg border-x border-b border-white/5 p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
            {displayServices.map((service, index) => (
              <a
                key={service.id}
                href={`#${service.id}`}
                className="group flex items-baseline gap-4 rounded-card px-5 py-4 transition-colors duration-300 hover:bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset"
              >
                <span className="text-mono-label text-neutral-600 group-hover:text-primary-500 transition-colors duration-300" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="text-body-small text-neutral-300 group-hover:text-primary-300 transition-colors duration-300 font-semibold">
                  {service.title}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding bg-black relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-4xl mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="text-center">
            <div className="surface-raised-gold edge-lit rounded-card-lg p-10 lg:p-14">
              <blockquote className="text-2xl lg:text-3xl text-primary-300 font-semibold mb-6 font-open-sans tracking-tight text-balance">
                &ldquo;Most companies optimise rewards for retention, fairness or competitiveness. Few design them for value creation.&rdquo;
              </blockquote>
              <p className="text-body text-neutral-300 text-balance">
                Copying the market gets you a package that looks like everyone else&rsquo;s and costs the same.
                The work below is about deciding where to break from it, and building programmes that move
                with the business instead of lagging a year behind it.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-black relative overflow-hidden grain scroll-mt-20">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-16 lg:mb-20">
            <Reveal className="lg:col-span-7">
              <p className="text-mono-label text-primary-500 mb-5">
                <span className="text-neutral-600">01 / 03</span>
                <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
                The practices
              </p>
              <h2 className="text-section-title text-white mb-6 text-balance">
                The disciplines, in detail.
              </h2>
              <p className="text-body-large text-neutral-300">
                Every engagement starts with the same question: what is this programme supposed to make
                happen? The architecture follows from the answer. Below is what each practice covers and
                what you are left holding at the end of it.
              </p>
            </Reveal>

            <Reveal className="lg:col-span-5" delay={120}>
              <BrandPrimitives
                variant="trio"
                className="w-full aspect-square max-w-[380px] mx-auto"
                label="Three forms in the brand palette, standing for the three parts of a package: cash, equity and benefits."
              />
              <p className="text-body-small text-neutral-500 max-w-[380px] mx-auto mt-3 text-center">
                Cash, equity and benefits. Every practice below moves one of the three without
                knocking the other two out of shape.
              </p>
            </Reveal>
          </div>

          <div className="space-y-20 lg:space-y-24">
            {displayServices.map((service, index) => {
              const IconComponent = iconMap[service.id] || Target
              const flip = index % 2 === 1

              return (
                <React.Fragment key={service.id}>
                  <Reveal
                    as="article"
                    id={service.id}
                    className="scroll-mt-24"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                      {/* Content */}
                      <div className={`lg:col-span-7 ${flip ? 'lg:col-start-6' : 'lg:col-start-1'}`}>
                        <div className="flex items-center gap-5 mb-7">
                          <div className="w-14 h-14 surface-raised-gold rounded-xl flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-6 h-6 text-primary-400" aria-hidden="true" />
                          </div>
                          <span className="text-mono-label text-neutral-600" aria-hidden="true">
                            {String(index + 1).padStart(2, '0')} / {String(displayServices.length).padStart(2, '0')}
                          </span>
                          <span className="hairline flex-1" aria-hidden="true"></span>
                        </div>

                        <h3 className="text-display-3 text-white mb-4 text-balance">{service.title}</h3>
                        <p className="text-body-large text-primary-400 font-semibold mb-6 text-balance">{service.subtitle}</p>
                        <p className="text-body text-neutral-300 mb-10">{service.description}</p>

                        {/* Sub-Services Section */}
                        {service.subServices && (
                          <div className="mb-10">
                            <h4 className="text-mono-label text-primary-500 mb-5">
                              Service components
                            </h4>
                            <div className="space-y-3">
                              {service.subServices.map((subService, idx) => (
                                <div key={idx} className="surface-raised rounded-card p-5 sm:p-6">
                                  <h5 className="text-white font-semibold mb-2 font-open-sans">{subService.name}</h5>
                                  <p className="text-body-small text-neutral-400">{subService.description}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mb-10">
                          <h4 className="text-mono-label text-primary-500 mb-5">
                            What that includes
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                            {service.features?.map((feature, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <CheckCircle className="w-4 h-4 text-primary-500 flex-shrink-0 mt-1" aria-hidden="true" />
                                <span className="text-body-small text-neutral-300">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <Link
                          to="/contact"
                          className="inline-flex items-center justify-center rounded-md px-7 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-primary-500/25 transition-colors duration-300 hover:border-primary-500/70 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
                        >
                          <span>
                            Talk this through<span className="sr-only"> — {service.title}</span>
                          </span>
                          <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                        </Link>
                      </div>

                      {/* Outcomes panel — sits alongside the detail on wide screens */}
                      <div className={`lg:col-span-5 ${flip ? 'lg:col-start-1 lg:row-start-1' : 'lg:col-start-8'}`}>
                        <div className="surface-raised-gold edge-lit rounded-card-lg p-8">
                          <h4 className="text-heading-1 text-white mb-6">What you end up with</h4>
                          <div className="space-y-4 mb-2">
                            {service.benefits?.map((benefit, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2.5 flex-shrink-0" aria-hidden="true"></span>
                                <span className="text-body-small text-neutral-300">{benefit}</span>
                              </div>
                            ))}
                          </div>

                          {service.process && service.process.length > 0 && (
                            <>
                              <div className="hairline my-8" aria-hidden="true"></div>
                              <h5 className="text-mono-label text-primary-500 mb-5">
                                How it runs
                              </h5>
                              <div className="space-y-3">
                                {service.process.map((step, idx) => (
                                  <div key={idx} className="flex items-start gap-3 surface-inset rounded-lg px-4 py-3">
                                    <span className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-black text-xs font-bold flex-shrink-0" aria-hidden="true">
                                      {idx + 1}
                                    </span>
                                    <span className="text-body-small text-neutral-300">{step}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Reveal>

                  {/* Breather between write-ups: the practices are sold
                      separately, but they stack into one package. */}
                  {index === 2 && index < displayServices.length - 1 && (
                    <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                      <div className="lg:col-span-7">
                        <div className="surface-raised edge-lit rounded-card-lg p-6 sm:p-8">
                          <RewardsArchitecture className="w-full h-auto" />
                        </div>
                      </div>

                      <div className="lg:col-span-5">
                        <p className="text-mono-label text-primary-500 mb-5">
                          One package, four layers
                        </p>
                        <h3 className="text-display-3 text-white mb-5 text-balance">
                          The practices are separate. The package is not.
                        </h3>
                        <p className="text-body text-neutral-300">
                          Base pay sets the floor, variable pay carries the year, equity carries the
                          years after it, and benefits sit under all of them. Move one layer and the
                          others move with it, which is why we cost a package as a stack rather than
                          as a run of separate line items.
                        </p>
                      </div>
                    </Reveal>
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </div>
      </section>

      {/* What a band architecture actually looks like. */}
      <section className="section-padding bg-black relative overflow-hidden grain" aria-labelledby="bands-heading">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="max-w-3xl mb-12 lg:mb-16">
            <p className="text-mono-label text-primary-500 mb-5">
              <span className="text-neutral-600">02 / 04</span>
              <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
              Structure
            </p>
            <h2 id="bands-heading" className="text-section-title text-white mb-6 text-balance">
              What a band architecture looks like
            </h2>
            <p className="text-body-large text-neutral-300">
              Grades, ranges and the overlap between them. Range spread widens as you
              go up, because senior roles vary more in scope than junior ones do. The
              overlap is deliberate: it lets a strong performer in one grade out-earn a
              new joiner in the grade above without breaking the structure.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="surface-raised rounded-card-lg edge-lit p-6 sm:p-8 lg:p-10 overflow-x-auto">
              <BandArchitecture className="w-full h-auto min-w-[560px]" />
            </div>
            <p className="text-body-small text-neutral-500 mt-4 text-center">
              Illustrative structure, indexed to the lowest midpoint. Real ranges are
              built from your job architecture and market data.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding bg-black relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="max-w-3xl mb-16 lg:mb-20">
            <p className="text-mono-label text-primary-500 mb-5">
              <span className="text-neutral-600">02 / 03</span>
              <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
              Sector knowledge
            </p>
            <h2 className="text-section-title text-white mb-6 text-balance">
              Where we already know the traps.
            </h2>
            <p className="text-body-large text-neutral-300">
              Pay problems rhyme within an industry. These are the four where we have seen the same
              arguments enough times to skip the first month of discovery.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {industries.map((industry, index) => (
              // `.scene` sits on the Reveal, not the grid: perspective only
              // reaches direct children, and the reveal transform would
              // otherwise flatten the tilt.
              <Reveal key={index} className="scene h-full" delay={index * 90}>
                {/* No `.sheen` here — its overflow:hidden would flatten
                    preserve-3d and cancel the .tilt-raise lift. */}
                <TiltCard className="surface-raised edge-lit rounded-card-lg h-full">
                  <div className="depth-layer p-8 h-full flex flex-col">
                    <div className="w-14 h-14 surface-raised-gold rounded-xl flex items-center justify-center mb-6 tilt-raise">
                      <industry.icon className="w-6 h-6 text-primary-400" aria-hidden="true" />
                    </div>

                    <h3 className="text-heading-1 text-white mb-3">{industry.name}</h3>
                    <p className="text-body-small text-neutral-400">{industry.description}</p>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-black text-white relative overflow-hidden grain">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-16 lg:mb-20">
            <Reveal className="lg:col-span-7">
              <p className="text-mono-label text-primary-500 mb-5">
                <span className="text-neutral-600">03 / 03</span>
                <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
                How we work
              </p>
              <h2 className="text-section-title text-white mb-6 text-balance">{processTitle}</h2>
              <p className="text-body-large text-neutral-300">
                {processSubtitle}
              </p>
            </Reveal>

            <Reveal className="lg:col-span-5" delay={120}>
              <BrandPrimitives
                variant="column"
                className="w-full aspect-square max-w-[340px] mx-auto"
                label="Three forms stacked in a column, standing for the phases of an engagement running in sequence."
              />
              <p className="text-body-small text-neutral-500 max-w-[340px] mx-auto mt-3 text-center">
                The phases run in order. Nothing gets designed before the current picture is written
                down and agreed.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {processSteps.map((phase, index) => (
              <Reveal key={index} delay={index * 110}>
                <div className="surface-raised edge-lit rounded-card-lg p-8 h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 surface-raised-gold rounded-xl flex items-center justify-center text-primary-300 font-bold text-lg font-open-sans">
                      {phase.step || String(index + 1).padStart(2, '0')}
                    </div>
                    <span className="hairline flex-1" aria-hidden="true"></span>
                  </div>

                  <h3 className="text-heading-1 text-white mb-4">{phase.title}</h3>
                  <p className="text-body-small text-neutral-400">{phase.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-black relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-4xl mx-auto w-full px-6 sm:px-8 lg:px-12 text-center relative">
          <Reveal>
            <div className="surface-raised-gold edge-lit rounded-card-lg p-10 lg:p-14">
              <BrandPrimitives
                variant="cluster"
                className="w-full aspect-square max-w-[220px] mx-auto mb-6"
                label="Cube, sphere and cone grouped together in the brand palette."
              />
              <h2 className="text-display-lg text-white mb-8 text-balance">
                Start with the problem you already know about.
              </h2>
              <p className="text-body-large text-neutral-300 mb-10 max-w-2xl mx-auto text-balance">
                Thirty minutes, no deck. Tell us where the pay conversation keeps stalling — a band that
                no longer holds, a bonus plan nobody trusts, a grant table you would rather not put in
                front of the board. We will tell you what fixing it actually involves.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="btn-accent sheen px-8 py-3.5">
                  Schedule Consultation
                  <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-primary-500/25 transition-colors duration-300 hover:border-primary-500/70 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
                >
                  Meet the team
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  )
}

export default ServicesPage
